import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Collapse,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import { ArrowDown2, ArrowUp2, Link21, Messages2 } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { ThreadMessage } from '../../types/task.type';

interface TaskThreadMessagesProps {
  threadLink: string | null;
  threadMessages: ThreadMessage[] | null;
}

export default function TaskThreadMessages({ threadLink, threadMessages }: TaskThreadMessagesProps) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  if (!threadLink || !threadMessages || threadMessages.length === 0) {
    return null;
  }

  const handleOpenInChat = () => {
    if (threadLink) {
      window.open(threadLink, '_blank');
    }
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 1,
          px: 1.5,
          borderRadius: '8px',
          bgcolor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: '#F3F4F6',
            borderColor: '#D1D5DB',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Messages2 size={16} color="#3B82F6" />
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151' }}>
            {t('tasks.thread.title')}
          </Typography>
          <Chip
            label={threadMessages.length}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: '#DBEAFE',
              color: '#2563EB',
              border: 'none',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenInChat();
            }}
            sx={{ p: 0.5 }}
          >
            <Link21 size={16} color="#3B82F6" />
          </IconButton>
          <IconButton size="small" sx={{ p: 0.5 }}>
            {expanded ? <ArrowUp2 size={16} color="#6B7280" /> : <ArrowDown2 size={16} color="#6B7280" />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 2,
            bgcolor: '#FAFBFC',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {threadMessages.map((msg, index) => (
            <Box key={msg.message_id}>
              {index > 0 && <Divider sx={{ my: 1.5 }} />}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Avatar
                  src={msg.sender_avatar_url || undefined}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    flexShrink: 0,
                    bgcolor: msg.sender_type === 'BOT' ? '#EEF2FF' : '#DBEAFE',
                    color: msg.sender_type === 'BOT' ? '#6366F1' : '#2563EB',
                  }}
                >
                  {!msg.sender_avatar_url && msg.sender_name.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#111827' }}>
                      {msg.sender_name}
                    </Typography>
                    {msg.sender_type === 'BOT' && (
                      <Chip
                        label="BOT"
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: '#DBEAFE',
                          color: '#2563EB',
                          border: 'none',
                        }}
                      />
                    )}
                    <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                      {formatTime(msg.create_time)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.8125rem',
                      color: '#374151',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {msg.text}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Paper>
      </Collapse>
    </Box>
  );
}
